/* eslint-disable no-underscore-dangle */
import { Button, Col, Form, InputNumber, Radio, Row, Tooltip, Typography } from 'antd';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { twoFactorAuth, twoFactorCodeGenerate } from 'services/auth';
import styled from 'styled-components';

const styles = {
  formHeight: {
    height: 40,
  },
  submintBtn: {
    marginTop: 24,
    height: 40,
  },
  spaceBetween: {
    display: 'flex',
    gap: 8,
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  remaindTime: {
    display: 'inline-block',
    width: 32,
    textAlign: 'center',
  },
};

/**
 * Two Factor 인증
 * @param {object} twoFactorChannel 인증 채널 정보
 * @param {ReactNode} setError 각종 에러 세팅
 * @param {boolean} setTwoFactorAuthenticated Two Factor 인증되었는지 여부를 의미하며, 상위 컴포넌트(Login)에 전달하여 인증 후 로직을 일원화하기 위함
 * @returns ReactNode
 */
function TwoFactor({ twoFactorChannel, setError, setTwoFactorAuthenticated, loginInfo }) {
  const [generated, setGenerated] = useState(false); // 인증코드 생성 결과 {result: true, expireDate: "2022-05-02T08:56:06.784Z"}
  const [expired, setExpired] = useState(false); // 인증코드 만료 여부
  const [isRegenerate, setIsRegenerate] = useState(false); // 인증코드 생성/재생성 요청의 API가 달라 재요청 상태 추가함

  useEffect(() => {
    if (expired) {
      setError('인증시간이 만료되었습니다.\n다시 시도해 주시기 바랍니다.');
    }
  }, [expired]);

  // FC: 인증코드 생성 요청 폼
  const CodeGenerateForm = () => {
    const onCodeGenerate = async (values) => {
      setError(undefined); // sumit 할때마다 error를 clear 한다.
      try {
        const res = await twoFactorCodeGenerate({ ...values, regenerate: isRegenerate, loginInfo });
        if (res.result) {
          setGenerated(res);
        } else {
          setError(res.msg);
        }
      } catch (e) {
        setError(e?.response?.data?.msg);
      }
    };
    return (
      <Form onFinish={onCodeGenerate}>
        <Row gutter={[0, 8]}>
          <Col span={24}>
            <Typography.Text style={{ fontWeight: 500 }}>2단계 인증이 필요합니다.</Typography.Text>
          </Col>
          <Col span={24}>
            <Form.Item label="인증 방식" name="channel" initialValue={twoFactorChannel[0].value}>
              <Radio.Group options={twoFactorChannel} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Button type="primary" htmlType="submit" block style={styles.submintBtn}>
              인증코드 요청
            </Button>
          </Col>
        </Row>
      </Form>
    );
  };

  // FC: 인증코드 입력 폼
  const InputCodeForm = () => {
    const onAuth = async (values) => {
      setError(undefined); // sumit 할때마다 error를 clear 한다.
      try {
        const res = await twoFactorAuth({ ...values, loginInfo });
        if (res.status === 201) {
          setTwoFactorAuthenticated(true);
        } else {
          setError(res.data.msg);
        }
      } catch (e) {
        setError(e?.response?.data?.msg);
      }
    };
    // 인증코드 재요청
    const onCodeRegenerate = () => {
      setError(undefined);
      setGenerated(false);
      setIsRegenerate(true);
      setExpired(false);
    };

    return (
      <>
        {expired ? (
          <Button type="primary" block style={styles.submintBtn} onClick={onCodeRegenerate}>
            인증코드 재요청
          </Button>
        ) : (
          <Form
            layout="vertical"
            labelCol={{ span: 8 }}
            wrapperCol={{ span: 16 }}
            onFinish={onAuth}
          >
            <Row gutter={[0, 8]}>
              <Col span={24}>
                <Typography.Text style={{ fontWeight: 500 }}>
                  수신받은 인증코드를 입력하십시오.
                </Typography.Text>
              </Col>
              <Col span={24} style={styles.spaceBetween}>
                <Form.Item noStyle style={{ flex: 1 }} name="authCode">
                  <StyledInputNumber
                    size="large"
                    placeholder="인증코드 입력"
                    controls={false}
                    addonAfter={<RemainedTime generated={generated} setExpired={setExpired} />}
                  />
                </Form.Item>
                <Button style={styles.formHeight} type="text" onClick={onCodeRegenerate}>
                  재요청
                </Button>
              </Col>
              <Col span={24}>
                <Button type="primary" htmlType="submit" block style={styles.submintBtn}>
                  확인
                </Button>
              </Col>
            </Row>
          </Form>
        )}
      </>
    );
  };

  return <>{generated.result ? <InputCodeForm /> : <CodeGenerateForm />}</>;
}

/**
 * 발급된 인증코드의 남은 시간을 리턴하는 함수
 * 인증코드 유효 기간(3분) 동안 1초마다 렌더링 되기 때문에 별도 Component로 뺌
 * @param {boolean} generated 인증코드 발급 여부에 따라 타이머를 동작시킨다.
 * @param {boolean} setExpired 인증코드의 유효 시간이 만료되었는지 판별한다.
 * @returns ReactNode
 */
function RemainedTime({ generated, setExpired }) {
  const [result, setResult] = useState(undefined); // 만료시각과 현재시각 비교 값(예: 2:58)

  // 만료시각이 임박했는가?
  const impend = result?.split(':')[0] === '0' && Number(result.split(':')[1]) < 10 && true;

  // generated 되면 인증시간 타이머를 동작시킨다.
  useEffect(() => {
    const timer = setInterval(() => {
      if (generated.result) {
        const expireTime = moment(generated.expireDate); // 서버로 부터 받은 만료 시각
        const now = moment();
        const diff = moment.duration(expireTime.diff(now));

        if (diff._milliseconds > 0) {
          setResult(`${diff._data.minutes}:${diff._data.seconds}`);
        } else {
          setResult('0:0');
          setExpired(true);
          clearInterval(timer);
        }
      }
      return () => {
        clearInterval(timer);
      };
    }, 1000);
  }, [generated]);

  return (
    <Tooltip title="남은 시간">
      <Typography.Text style={styles.remaindTime} type={impend ? 'danger' : null}>
        {result}
      </Typography.Text>
    </Tooltip>
  );
}

const StyledInputNumber = styled(InputNumber)`
  font-size: 12px;
  flex: 1;
  .ant-input-number-lg input {
    height: 38px;
  }
`;

export default TwoFactor;
